import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  initializeAuth,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  setPersistence,
  connectAuthEmulator // Import connectAuthEmulator
} from 'firebase/auth';
import { 
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  limit,
  connectFirestoreEmulator // Import connectFirestoreEmulator
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage'; // Import connectStorageEmulator
import { Platform } from 'react-native';
import { Trip, User } from '../types';

// Firebase config from environment
console.log('Environment check:', {
  hasApiKey: !!process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  apiKeyValue: process.env.EXPO_PUBLIC_FIREBASE_API_KEY?.substring(0, 10) + '...',
});

const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY || "your-api-key",
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || "your-auth-domain",
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID || "your-project-id",
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET || "your-storage-bucket",
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "your-sender-id",
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID || "your-app-id"
};

console.log('Firebase config:', firebaseConfig);

const app = initializeApp(firebaseConfig);

let auth: any;
if (Platform.OS === 'web') {
  auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence);
} else {
  auth = getAuth(app);
}

const db = getFirestore(app);
const storage = getStorage(app);

// --- CONNECT TO EMULATORS ---
// This is the crucial part that was missing.
// It ensures the frontend app talks to the local emulators.
if (process.env.NODE_ENV === 'development' || process.env.EXPO_PUBLIC_USE_EMULATORS === 'true') {
  console.log('Connecting to local Firebase emulators...');
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    console.log('Successfully connected to emulators.');
  } catch (e) {
    console.error('Error connecting to emulators:', e);
  }
}
// --------------------------

// Auth functions
export const signIn = async (email: string, password: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const signUp = async (email: string, password: string, name: string) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  
  const generateUniqueId = (): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };
  
  await setDoc(doc(db, 'users', userCredential.user.uid), {
    email,
    name,
    emailInboxId: generateUniqueId(),
    createdAt: serverTimestamp()
  });
  
  return userCredential.user;
};

export const signOutUser = async () => {
  await firebaseSignOut(auth);
};

export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      const userData = userDoc.data();
      callback({
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: userData?.name || firebaseUser.displayName || '',
        avatar: userData?.avatar || firebaseUser.photoURL || undefined,
        emailInboxId: userData?.emailInboxId || undefined
      });
    } else {
      callback(null);
    }
  });
};

// ... (rest of the file is unchanged)

// Trip functions
export const createTrip = async (tripData: Omit<Trip, 'id' | 'createdAt' | 'updatedAt'>) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const docRef = await addDoc(collection(db, 'trips'), {
    ...tripData,
    userId: user.uid,
    collaborators: [user.uid], // Ensure creator is always a collaborator
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return docRef.id;
};

export const updateTrip = async (tripId: string, updates: Partial<Trip>) => {
  await updateDoc(doc(db, 'trips', tripId), {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

export const deleteTrip = async (tripId: string) => {
  await deleteDoc(doc(db, 'trips', tripId));
};

export const getUserTrips = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  try {
    const q = query(
      collection(db, 'trips'),
      where('userId', '==', user.uid),
      orderBy('startDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Trip));
  } catch (error: any) {
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.log('Index not created yet, fetching without ordering');
      const q = query(
        collection(db, 'trips'),
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(q);
      const trips = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Trip));
      
      return trips.sort((a, b) => {
        const dateA = a.startDate instanceof Timestamp ? a.startDate.toDate() : new Date(a.startDate);
        const dateB = b.startDate instanceof Timestamp ? b.startDate.toDate() : new Date(b.startDate);
        return dateB.getTime() - dateA.getTime();
      });
    }
    throw error;
  }
};

export const getTrip = async (tripId: string) => {
  const docSnap = await getDoc(doc(db, 'trips', tripId));
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as Trip;
  }
  return null;
};

// Collaboration functions
export const shareTrip = async (tripId: string, email: string) => {
  const trip = await getTrip(tripId);
  if (!trip) throw new Error('Trip not found');

  const usersQuery = query(collection(db, 'users'), where('email', '==', email), limit(1));
  const userSnapshot = await getDocs(usersQuery);
  
  if (userSnapshot.empty) {
    throw new Error('User not found with that email address');
  }
  
  const userId = userSnapshot.docs[0].id;
  const collaborators = trip.collaborators || [];
  
  if (!collaborators.includes(userId)) {
    await updateTrip(tripId, {
      collaborators: [...collaborators, userId]
    });
  }
};

export const getSharedTrips = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const q = query(
    collection(db, 'trips'),
    where('collaborators', 'array-contains', user.uid)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Trip));
};

export const subscribeToUserTrips = (userId: string, callback: (trips: Trip[]) => void) => {
  const q = query(
    collection(db, 'trips'),
    where('userId', '==', userId),
    orderBy('startDate', 'desc')
  );

  return onSnapshot(q, 
    (snapshot) => {
      const trips = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Trip));
      callback(trips);
    },
    (error) => {
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.log('Index not created yet, subscribing without ordering');
        const simpleQuery = query(
          collection(db, 'trips'),
          where('userId', '==', userId)
        );
        
        return onSnapshot(simpleQuery, (snapshot) => {
          const trips = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Trip));
          
          const sortedTrips = trips.sort((a, b) => {
            const dateA = a.startDate instanceof Timestamp ? a.startDate.toDate() : new Date(a.startDate);
            const dateB = b.startDate instanceof Timestamp ? b.startDate.toDate() : new Date(b.startDate);
            return dateB.getTime() - dateA.getTime();
          });
          
          callback(sortedTrips);
        });
      }
      console.error('Error in trips subscription:', error);
    }
  );
};

export const subscribeToSharedTrips = (userId: string, callback: (trips: Trip[]) => void) => {
  const q = query(
    collection(db, 'trips'),
    where('collaborators', 'array-contains', userId)
  );

  return onSnapshot(q, (snapshot) => {
    const trips = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Trip))
      // --- FIX: Filter out trips owned by the user ---
      .filter(trip => (trip as any).userId !== userId);
      
    callback(trips);
  });
};

export { auth, db, storage, db as firestore };